import { AccountCard } from '../account/account_card.js';
import { assert } from '../app/logger.js';
import { navigation } from '../app/navigation.js';
import { Dialog } from '../controls/dialog.js';
import { listenToEvent } from '../util/listen_to_event.js';

/**
 * Accept trip share dialog.
 */
class AcceptTripShareDialog extends Dialog {
  async show(inviteHash, shareInfo) {
    assert(inviteHash, `[accept-share] no inviteHash`);
    assert(shareInfo, `[accept-share] no share info`);

    this.#inviteHash = inviteHash;
    this.#shareInfo = shareInfo;

    // Accept share error dialog.
    let { errorCode } = this.#shareInfo;
    if (errorCode) {
      switch (errorCode) {
        case 'SHARE_NOT_FOUND':
          this.bodyElement.innerHTML = `
            <p class='dialog-content'>Invitation <code>${
              this.#inviteHash
            }</code> was not found.</p>
          `;
          break;
        case 'SHARE_HAS_ALREADY_BEEN_ACCEPTED':
          this.bodyElement.innerHTML = `
            <p class='dialog-content'>
              The invitation to the <span class='designation'>
              ${this.#shareInfo.title}</span> has already been accepted.
            </p>
          `;
          break;

        case 'SHARE_HAS_ALREADY_BEEN_REJECTED':
          this.bodyElement.innerHTML = `
            <p class='dialog-content'>
              The invitation to the <span class='designation'>
              ${this.#shareInfo.title}</span> has already been rejected.
            </p>
          `;
          break;

        case 'INVITEE_AND_INVITER_ARE_SAME_USER':
          this.bodyElement.innerHTML = `
            <p class='dialog-content'>Cannot accept an invite to join
            the <span class='designation'>${this.#shareInfo.title}</span>
            because this is your own trip.
            <a href='/login'>Switch</a> to another account?</p>
          `;
          listenToEvent(
            this.bodyElement.querySelector('a'),
            'click',
            () => navigation.toLogin(),
            {
              preventDefault: true,
            }
          );
          break;

        default:
          this.bodyElement.innerHTML = `
            <p class="dialog-content">Unexpected problem. Please reach us at
              <a href="mailto:support@camperaid.com">support@camperaid.com</a>.</p>
          `;
          break;
      }
      this.acceptButton.hidden = true;
      this.cancelButton.textContent = 'Close';
      super.show();
      return;
    }

    // Accept share dialog.
    let card = new AccountCard({
      name: this.#shareInfo.inviterName,
      email: this.#shareInfo.inviterEmail,
      avatar: this.#shareInfo.inviterAvatar,
    });

    this.avatarElement.textContent = card.avatar;
    this.designationElement.textContent = this.#shareInfo.title;
    this.nameElement.textContent = card.name;
    this.emailElement.textContent = card.email;

    super.show();
  }

  static tagName = 'accept-trip-share-dialog';

  static titleMarkup = `Join the trip`;

  static bodyMarkup = `
    <img src=''>
    <p class='dialog-content'>
      <span class='name'></span>
      <span class='email'>&lt;<span class='email-value'></span>&gt;</span> invites you to
      join the trip <span class='designation'></span>.
    </p>
  `;

  static get buttons() {
    return {
      accept: {
        label: 'Accept',
        default: true,
      },
      cancel: {
        label: 'Maybe later',
      },
    };
  }

  static get selectors() {
    const baseSelectors = super.selectors;
    const { Body } = baseSelectors;
    return Object.assign({}, super.selectors, {
      Avatar: `${Body} img`,
      Content: `${Body} .dialog-content`,
      Designation: `${Body} .designation`,
      Name: `${Body} .name`,
      Email: `${Body} .email > .email-value`,
    });
  }

  static get elements() {
    return {
      avatarElement: this.selectors.Avatar,
      designationElement: this.selectors.Designation,
      nameElement: this.selectors.Name,
      emailElement: this.selectors.Email,
    };
  }

  get data() {
    return this.#inviteHash;
  }

  #inviteHash;
  #shareInfo;
}

AcceptTripShareDialog.defineCustomElement();

export { AcceptTripShareDialog };
